import packageJson from '../../package.json';
import i18n from '../i18n';
import releaseNotes from '../data/releaseNotes';
import { getSetting, setSetting, settingKeys } from '../utils/settings';

export const currentVersion = packageJson.version;

const parseVersion = (version) =>
  String(version)
    .split('.')
    .map((n) => parseInt(n, 10) || 0);

// Patch is ignored, so 5.1.0 → 5.1.7 is no change at all
function compareMinor(a, b) {
  const [aMajor = 0, aMinor = 0] = parseVersion(a);
  const [bMajor = 0, bMinor = 0] = parseVersion(b);

  return aMajor - bMajor || aMinor - bMinor;
}

/**
 * The newest release after `lastSeen` up to `current` that has changes for this role, or null.
 * With no `lastSeen` only a major (x.0) is announced — the device can't say which minors it has seen.
 */
export function getLatestReleaseNotes(lastSeen, current, isAdmin, notes = releaseNotes, language = i18n.language) {
  if (!lastSeen) {
    const [major = 0, minor = 0] = parseVersion(current);
    if (minor !== 0) return null;
    // Anything before x.0 counts as seen, so only x.0's own notes can show
    lastSeen = `${major - 1}.${Number.MAX_SAFE_INTEGER}`;
  }

  if (compareMinor(current, lastSeen) <= 0) return null;

  const latest = notes
    .filter(({ version }) => compareMinor(version, lastSeen) > 0 && compareMinor(version, current) <= 0)
    .map((release) => {
      // The release in the app's language, field by field, else as written in English
      const { users = [], admins = [] } = { ...release, ...release[language] };

      return { version: release.version, changes: isAdmin ? [...users, ...admins] : users };
    })
    .filter(({ changes }) => changes.length > 0)
    .sort((a, b) => compareMinor(b.version, a.version))[0];

  return latest || null;
}

export function markVersionSeen() {
  setSetting(settingKeys.lastSeenVersion, currentVersion);
}

/** The release to show this user now; stamps the version straight away when there's none */
export function loadReleaseNotes(isAdmin) {
  const release = getLatestReleaseNotes(getSetting(settingKeys.lastSeenVersion), currentVersion, isAdmin);

  if (!release) markVersionSeen();

  return release;
}
